import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Modal, PrimaryButton } from '..'
import { logout, setTimer } from '../../store/slices'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

const LOGOUT_TIME = 20 * 60 // 20 minutos de sesión
const WARNING_TIME = 2 * 60 // el modal aparece 2 min antes de cerrar
const ACTIVITY_SYNC_KEY = 'logoutTimer:lastActivity'

export const LogoutTimer: FC = () => {
  const dispatch = useAppDispatch()
  const timer = useAppSelector((state) => state.user.timer)
  const logged = useAppSelector((state) => state.user.logged)

  const timerRef = useRef(timer)
  const [remainingTime, setRemainingTime] = useState(LOGOUT_TIME)
  const [openLogoutModal, setOpenLogoutModal] = useState(false)

  const computeRemaining = useCallback(
    () => LOGOUT_TIME - (new Date().getTime() / 1000 - timerRef.current),
    []
  )

  const extendSession = useCallback(
    (currentTime: number, broadcast: boolean) => {
      timerRef.current = currentTime
      setRemainingTime(LOGOUT_TIME)
      setOpenLogoutModal(false)
      dispatch(setTimer(currentTime))

      if (broadcast) {
        try {
          localStorage.setItem(
            ACTIVITY_SYNC_KEY,
            JSON.stringify({ time: currentTime, nonce: Math.random() })
          )
        } catch {}
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (!logged) return

    const intervalId = setInterval(() => {
      setRemainingTime(computeRemaining())
    }, 1000)

    return () => clearInterval(intervalId)
  }, [logged, computeRemaining])

  useEffect(() => {
    if (!logged) {
      setOpenLogoutModal(false)
      setRemainingTime(LOGOUT_TIME)
      return
    }
    if (
      remainingTime > 0 &&
      remainingTime <= WARNING_TIME &&
      !openLogoutModal
    ) {
      setOpenLogoutModal(true)
    } else if (remainingTime <= 0) {
      setOpenLogoutModal(false)
      dispatch(logout())
    }
    // Don't add openLogoutModal to the dependencies array because it will cause an
    // infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime, dispatch, logged])

  useEffect(() => {
    timerRef.current = timer
  }, [timer])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        setRemainingTime(computeRemaining())
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [computeRemaining])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_SYNC_KEY || !e.newValue) return
      try {
        const { time } = JSON.parse(e.newValue)
        if (typeof time === 'number') extendSession(time, false)
      } catch {}
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [extendSession])

  const handleCloseModal = () => {
    extendSession(new Date().getTime() / 1000, true)
  }

  return (
    <Modal
      open={openLogoutModal && logged}
      setOpen={() => {}}
      className="flex flex-1 flex-col"
      style={{ maxWidth: '28rem' }}
    >
      <div className="flex flex-1 gap-6 items-center">
        <div className="w-10 h-10">
          <InformationCircleIcon className="w-10 h-10 text-main-500" />
        </div>
        <span>
          La sesión finalizará en{' '}
          <b>{Math.max(0, remainingTime).toFixed(0)} segundos</b>. Presione
          continuar para extender el tiempo de la sesión.
        </span>
      </div>

      <hr className="w-full my-4" />

      <div className="flex flex-1 w-full justify-end">
        <PrimaryButton onClick={handleCloseModal}>Continuar</PrimaryButton>
      </div>
    </Modal>
  )
}
